import React, { useEffect } from 'react';
import { Outlet } from 'react-router';
import { EnrichContext } from '@groupby/enrich-console';
import { compose } from 'ramda';
import TagManager from 'react-gtm-module';
import jsSHA from 'jssha';
import { useAppDispatch } from '@/store/hooks';
import { Notifications } from '@/components/notifications';
import { ConfirmationAlertProvider } from '@/confirmation-alert';
import { ErrorAlertProvider } from '@/error-alert';
import { AdhocMessageProvider } from '@/adhoc-message/adhoc-message.provider';
import { AppFrame, FrameContent } from '@/components/frame';
import { LocaleProvider, Localization, LocalizationProvider, useLocale, } from '@/localization';
import { addError, addSuccess } from '@/store/slices/notificationsSlice';
import { getConfig } from '@/config';
import { Routing } from '@/constants';
import { ls } from '@/stores/local-storage';
import { getToken } from '@/auth/auth-access';
import { useAuthToken, useUserEmail } from '@/auth/hooks/authentication';
import { useFeatureFlagsProvider } from '@/search-for-retail/feature-flags';
import { FeatureFlagsUpdateContext } from '@/search-for-retail/feature-flags/feature-flags-context.layout';
import { logError } from '@/utils/logger';
import { CookieConsentBanner } from '@/components/cookie-consent';
export const AppLayout = () => {
    const config = getConfig();
    const { featureFlagsService } = useFeatureFlagsProvider();
    const dispatch = useAppDispatch();
    const { locale } = useLocale();
    const userEmail = useUserEmail();
    useEffect(() => {
        if (userEmail) {
            // Hash the email address to avoid sending PII to Google Analytics
            // eslint-disable-next-line new-cap
            const shaObj = new jsSHA('SHA-256', 'TEXT');
            shaObj.update(userEmail);
            const emailHashed = shaObj.getHash('HEX');
            // check if user is external by comparing user email domain to 'groupbyinc.com'
            const isExternal = !userEmail.endsWith('@groupbyinc.com');
            TagManager.dataLayer({
                dataLayer: {
                    'event': 'userAuthenticated',
                    'user_id': emailHashed,
                    'external_user': isExternal,
                },
            });
        }
    }, [userEmail]);
    const ENRICH_PROVIDER_VALUE = {
        // useAuthToken should be removed after command-center-enrich/pull/927 merged
        useAuthToken,
        getToken,
        logError,
        notification: {
            addError: compose(dispatch, addError),
            addSuccess: compose(dispatch, addSuccess),
        },
        localization: {
            selectedLanguage: locale,
            defaultLanguage: Localization.DEFAULT_LANGUAGE_CODE,
        },
        config: {
            isNonPublicEnv: config.isNonPublicEnv,
            enrichApiPrefix: config.enrichApiPrefix,
            enrichAIApiPrefix: config.enrichAIApiPrefix,
            enrichPath: Routing.ENRICH_PATH,
        },
        localStorage: ls,
        featureFlagsService,
    };
    return (<FeatureFlagsUpdateContext>
      <LocaleProvider>
        <EnrichContext.Provider value={ENRICH_PROVIDER_VALUE}>
          <LocalizationProvider>
            <AdhocMessageProvider>
              <ConfirmationAlertProvider>
                <ErrorAlertProvider>
                  <AppFrame>
                    <Notifications />
                    <CookieConsentBanner />
                    <FrameContent>
                      <Outlet />
                    </FrameContent>
                  </AppFrame>
                </ErrorAlertProvider>
              </ConfirmationAlertProvider>
            </AdhocMessageProvider>
          </LocalizationProvider>
        </EnrichContext.Provider>
      </LocaleProvider>
    </FeatureFlagsUpdateContext>);
};
